import { useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR, { mutate } from "swr";
import client, { fetcher } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { FileText, Loader2, Download, ChevronLeft, ChevronRight, ShieldCheck } from "lucide-react";
import { toast as _toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import "@/i18n/fase4";

const DEFAULT_MODULES = {
  portada: true, resumo_executivo: true, geoportal: true, esg: true,
  carbono: true, incidencias_ia: true, contedores_pesaxes: true,
  inventario_datos: true, conclusions: true,
};

export default function Informe() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [from, setFrom] = useState("2026-01-01");
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [concellos, setConcellos] = useState([]);
  const [language, setLanguage] = useState(i18n.language);
  const [modules, setModules] = useState(DEFAULT_MODULES);
  const [conclusionGl, setConclusionGl] = useState("");
  const [conclusionEs, setConclusionEs] = useState("");
  const [includePhotos, setIncludePhotos] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);

  const { data: concellosData } = useSWR("/salnes/concellos", fetcher);
  const { data: historyData } = useSWR("/informe/history?limit=20", fetcher);

  const toggleModule = (k) => setModules((m) => ({ ...m, [k]: !m[k] }));
  const toggleConcello = (key) => setConcellos((cs) => cs.includes(key) ? cs.filter((x) => x !== key) : [...cs, key]);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await client.post("/informe/generate", {
        period: { from, to },
        concellos, modules,
        conclusion_text_gl: conclusionGl,
        conclusion_text_es: conclusionEs,
        include_photos: includePhotos,
        language,
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/i.exec(cd);
      a.download = m?.[1] || `informe-institucional-salnes.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(t("fase4.inf_ok"));
      mutate("/informe/history?limit=20");
    } catch { toast.error(t("fase4.inf_error")); }
    finally { setLoading(false); }
  };

  const downloadPrevious = async (id, filename) => {
    try {
      const res = await client.get(`/informe/${id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `informe-${id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error(t("fase4.inf_error")); }
  };

  const activeModules = Object.keys(modules).filter((k) => modules[k]);

  return (
    <div data-testid="informe-page">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.24em] text-salnes-stone">{t("fase4.sidebar_informe")}</div>
        <h1 className="font-serif text-3xl text-salnes-ink">{t("fase4.inf_title")}</h1>
        <div className="text-sm text-salnes-stone mt-1">{t("fase4.inf_subtitle")}</div>
      </div>

      {/* Stepper */}
      <div className="rounded-2xl border hairline bg-white p-6 mb-6">
        <div className="flex items-center justify-between mb-6" data-testid="informe-stepper">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="flex-1 flex items-center gap-3">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium ${step >= n ? "bg-salnes-atlantic text-white" : "bg-salnes-sand/60 text-salnes-stone"}`}>{n}</div>
              <div className={`text-[11px] uppercase tracking-[0.18em] ${step >= n ? "text-salnes-ink" : "text-salnes-stone"}`}>
                {t(`fase4.inf_step${n}`)}
              </div>
              {n < 4 && <div className="flex-1 h-[1px] bg-salnes-stone/20" />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4" data-testid="informe-step-1">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-salnes-stone mb-2">{t("fase4.inf_period_from")}</div>
                <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border hairline rounded px-3 h-10 w-full" data-testid="informe-from" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-salnes-stone mb-2">{t("fase4.inf_period_to")}</div>
                <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border hairline rounded px-3 h-10 w-full" data-testid="informe-to" />
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-salnes-stone mb-2">{t("fase4.inf_language")}</div>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="w-full md:w-[280px]" data-testid="informe-language"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gl">{t("fase4.inf_language_gl")}</SelectItem>
                  <SelectItem value="es">{t("fase4.inf_language_es")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-salnes-stone mb-2">{t("fase4.inf_concellos")}</div>
              <div className="flex flex-wrap gap-2">
                {(concellosData?.concellos || []).map((c) => (
                  <button
                    key={c.key} type="button"
                    onClick={() => toggleConcello(c.key)}
                    data-testid={`informe-concello-${c.key}`}
                    className={`px-3 py-1.5 rounded-full text-xs border hairline transition-colors ${concellos.includes(c.key) ? "bg-salnes-atlantic text-white border-salnes-atlantic" : "text-salnes-stone hover:bg-salnes-sand/40"}`}
                  >{c.name}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="informe-step-2">
            {Object.keys(DEFAULT_MODULES).map((k) => {
              const labelKey = `fase4.inf_module_${
                k === "resumo_executivo" ? "resumo" :
                k === "incidencias_ia" ? "incidencias" :
                k === "inventario_datos" ? "inventario" :
                k === "contedores_pesaxes" ? "contedores" : k
              }`;
              const subKey = k === "contedores_pesaxes" ? "fase4.inf_module_contedores_sub" : null;
              return (
                <label key={k} className="flex items-start gap-3 px-4 py-3 rounded-lg border hairline hover:bg-salnes-sand/20 cursor-pointer">
                  <Checkbox
                    checked={modules[k]}
                    onCheckedChange={() => toggleModule(k)}
                    data-testid={k === "contedores_pesaxes" ? "informe-include-contedores" : `informe-module-${k}`}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="text-sm text-salnes-ink font-medium">{t(labelKey)}</div>
                    {subKey && (
                      <div className="text-[11px] text-salnes-stone mt-1 leading-relaxed">
                        {t(subKey)}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
            <label
              className="flex items-start gap-3 px-4 py-3 rounded-lg border hairline bg-salnes-sand/20 cursor-pointer"
              data-testid="informe-include-photos-row"
            >
              <Checkbox
                checked={includePhotos}
                onCheckedChange={(v) => setIncludePhotos(!!v)}
                data-testid="informe-include-photos"
                className="mt-0.5"
              />
              <div className="flex-1">
                <div className="text-sm text-salnes-ink font-medium">{t("fase4.inf_include_photos")}</div>
                <div className="text-[11px] text-salnes-stone mt-1 leading-relaxed">
                  {t("fase4.inf_include_photos_sub")}
                </div>
              </div>
            </label>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="informe-step-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-salnes-stone mb-2">{t("fase4.inf_conclusion_gl")}</div>
              <Textarea rows={6} value={conclusionGl} onChange={(e) => setConclusionGl(e.target.value.slice(0, 5000))} placeholder={t("fase4.inf_conclusion_placeholder")} data-testid="informe-conclusion-gl" />
              <div className="text-[10px] text-salnes-stone text-right mt-1">{conclusionGl.length} / 5000 · {t("fase4.inf_conclusion_max")}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-salnes-stone mb-2">{t("fase4.inf_conclusion_es")}</div>
              <Textarea rows={6} value={conclusionEs} onChange={(e) => setConclusionEs(e.target.value.slice(0, 5000))} placeholder={t("fase4.inf_conclusion_placeholder")} data-testid="informe-conclusion-es" />
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4" data-testid="informe-step-4">
            <div className="grid md:grid-cols-2 gap-4">
              <ReviewRow label={t("fase4.inf_review_period")} value={`${from} → ${to}`} />
              <ReviewRow label={t("fase4.inf_review_lang")} value={language.toUpperCase()} />
              <ReviewRow label={t("fase4.inf_review_concellos")} value={concellos.length ? concellos.join(", ") : "Todos"} />
              <ReviewRow label={t("fase4.inf_review_modules")} value={activeModules.length} />
            </div>
            <div className="flex flex-wrap gap-2">
              {activeModules.map((k) => <Badge key={k} className="bg-salnes-emerald text-white text-[10px]">{k}</Badge>)}
            </div>
            <div
              className={`rounded-lg border hairline px-4 py-3 text-sm ${includePhotos ? "bg-salnes-emerald/10 text-salnes-emerald border-salnes-emerald/30" : "bg-salnes-sand/30 text-salnes-stone"}`}
              data-testid="informe-photos-summary"
            >
              {includePhotos ? t("fase4.inf_photos_yes", { n: "—" }) : t("fase4.inf_photos_no")}
            </div>
            <div className="pt-4">
              <Button
                size="lg"
                onClick={generate}
                disabled={loading}
                data-testid="informe-generate-btn"
                className="rounded-full bg-salnes-atlantic hover:bg-salnes-atlantic/90"
              >
                {loading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <FileText size={14} className="mr-2" />}
                {loading ? t("fase4.inf_generating") : t("fase4.inf_generate")}
              </Button>
              {user?.role !== "MANCOMUNIDADE" && (
                <div className="text-xs text-salnes-stone mt-2 italic" data-testid="informe-role-notice">
                  {t("fase4.inf_role_notice")}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-between mt-6 pt-4 border-t hairline">
          <Button variant="outline" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1} data-testid="informe-prev"><ChevronLeft size={14} className="mr-1" /> {t("fase4.inf_prev")}</Button>
          <Button variant="outline" onClick={() => setStep((s) => Math.min(4, s + 1))} disabled={step === 4} data-testid="informe-next">{t("fase4.inf_next")} <ChevronRight size={14} className="ml-1" /></Button>
        </div>
      </div>

      {/* History */}
      <div className="rounded-2xl border hairline bg-white overflow-hidden">
        <div className="px-6 py-4 border-b hairline">
          <div className="text-[11px] uppercase tracking-[0.2em] text-salnes-stone">{t("fase4.inf_history_title")}</div>
        </div>
        {(historyData?.items || []).length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-salnes-stone italic">{t("fase4.inf_history_empty")}</div>
        ) : (
          <table className="w-full text-sm" data-testid="informe-history-table">
            <thead className="bg-salnes-sand/40 text-salnes-stone text-[10px] uppercase tracking-[0.18em]">
              <tr>
                <th className="text-left px-4 py-3">{t("fase4.inf_history_col_date")}</th>
                <th className="text-left px-4 py-3">{t("fase4.inf_history_col_period")}</th>
                <th className="text-left px-4 py-3">{t("fase4.inf_history_col_author")}</th>
                <th className="text-left px-4 py-3">Idioma</th>
                <th className="text-right px-4 py-3">{t("fase4.inf_history_col_size")}</th>
                <th className="text-right px-4 py-3">{t("fase4.inf_history_col_actions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y hairline">
              {(historyData?.items || []).map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-3">{r.created_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-3">{r.period?.from} → {r.period?.to}</td>
                  <td className="px-4 py-3">
                    <div className="text-salnes-ink">{r.generated_by_name}</div>
                    <Badge className={`text-[9px] mt-1 ${r.approved ? "bg-salnes-emerald text-white" : "bg-salnes-stone/70 text-white"}`}>{r.approved ? t("fase4.inf_history_approved") : t("fase4.inf_history_draft")}</Badge>
                  </td>
                  <td className="px-4 py-3 uppercase text-[10px]">{r.language}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{Math.round((r.size_bytes || 0) / 1024)} KB</td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => downloadPrevious(r.id, r.filename)} data-testid={`informe-download-${r.id}`}>
                      <Download size={12} className="mr-1" /> {t("fase4.inf_history_download")}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setVerifyOpen(r); setVerifyResult(null); }} data-testid={`informe-verify-${r.id}`}>
                      <ShieldCheck size={12} className="mr-1" /> SHA-256
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!verifyOpen} onOpenChange={(o) => !o && setVerifyOpen(null)}>
        <DialogContent data-testid="informe-verify-dialog">
          <DialogHeader>
            <DialogTitle>{t("fase5.sha_verify")}</DialogTitle>
            <DialogDescription>{t("fase5.sha_verify_desc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-salnes-stone mb-1">{t("fase5.sha_expected")}</div>
              <div className="rounded-lg bg-salnes-ink text-salnes-ivory p-3 text-[11px] font-mono break-all" data-testid="informe-verify-sha">
                {verifyResult?.expected_sha256 || (verifyOpen ? "…" : "")}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-salnes-stone mb-1">{t("fase5.sha_upload")}</div>
              <input
                type="file"
                accept="application/pdf"
                data-testid="informe-verify-file"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f || !verifyOpen) return;
                  const fd = new FormData();
                  fd.append("file", f);
                  try {
                    const { data } = await client.post(`/informe/${verifyOpen.id}/verify`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                    setVerifyResult(data);
                  } catch { _toast.error(t("fase4.inf_error")); }
                }}
                className="text-sm"
              />
            </div>
            {verifyResult?.match === true && (
              <div className="rounded-lg bg-salnes-emerald/10 border border-salnes-emerald text-salnes-emerald px-4 py-2 text-sm" data-testid="informe-verify-match-ok">
                ✓ {t("fase5.sha_match_ok")}
              </div>
            )}
            {verifyResult?.match === false && (
              <div className="rounded-lg bg-salnes-coral/10 border border-salnes-coral text-salnes-coral px-4 py-2 text-sm" data-testid="informe-verify-match-fail">
                ✗ {t("fase5.sha_match_fail")}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerifyOpen(null)}>{t("fase5.sha_close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {(() => {
        // fetch expected SHA when dialog opens (lazy)
        if (verifyOpen && !verifyResult) {
          client.post(`/informe/${verifyOpen.id}/verify`).then((r) => setVerifyResult(r.data)).catch(() => {});
        }
        return null;
      })()}
    </div>
  );
}

const ReviewRow = ({ label, value }) => (
  <div className="rounded-lg border hairline p-3 bg-salnes-sand/20">
    <div className="text-[10px] uppercase tracking-[0.2em] text-salnes-stone">{label}</div>
    <div className="text-sm text-salnes-ink font-medium mt-0.5">{value}</div>
  </div>
);
